/** Focus trend chart math — pure (no React, no network) so it is unit-testable. */

export interface WeekPoint { weekStart: string; seconds: number }
export interface Bucket { label: string; seconds: number }

export interface Trend {
  minutes: number[];
  total: number;
  avg: number;
  best: number;
  bestIndex: number;
  /** Last bucket vs the average of the ones before — null when not computable. */
  pct: number | null;
}

/** Bar height clamp (percent of the plot area): stubs stay visible, the tallest
 *  bar leaves headroom for its value label so nothing clips or overlaps. */
export const BAR_MIN_PCT = 7;
export const BAR_MAX_PCT = 80;

/** Range aggregation: week/month = the week buckets as-is; year = groups of 4. */
export function buildBuckets(weeks: WeekPoint[], range: "week" | "month" | "year", locale: string): Bucket[] {
  if (!weeks.length) return [];
  if (range === "year") {
    const groups: Bucket[] = [];
    for (let i = 0; i < weeks.length; i += 4) {
      const chunk = weeks.slice(i, i + 4);
      groups.push({ label: `${i / 4 + 1}`, seconds: chunk.reduce((sum, week) => sum + week.seconds, 0) });
    }
    return groups;
  }
  const dateTag = locale === "en" ? "en-GB" : locale;
  return weeks.map((week) => ({
    label: new Intl.DateTimeFormat(dateTag, { day: "numeric", month: "short" }).format(new Date(`${week.weekStart}T12:00:00`)),
    seconds: week.seconds
  }));
}

/** Trend intelligence: total, average, best bucket, and last-vs-earlier-average. */
export function computeTrend(buckets: Bucket[]): Trend | null {
  if (buckets.length === 0) return null;
  const minutes = buckets.map((bucket) => Math.round(bucket.seconds / 60));
  const total = minutes.reduce((sum, value) => sum + value, 0);
  const avg = Math.round(total / minutes.length);
  const best = Math.max(...minutes);
  let pct: number | null = null;
  if (buckets.length >= 2) {
    const last = minutes[minutes.length - 1];
    const earlier = minutes.slice(0, -1);
    const prevAvg = earlier.reduce((sum, value) => sum + value, 0) / earlier.length;
    pct = prevAvg > 0 ? Math.round(((last - prevAvg) / prevAvg) * 100) : null;
  }
  const bestIndex = minutes.indexOf(best);
  return { minutes, total, avg, best, bestIndex, pct };
}

/** Clamped bar height percent — labels never clip above the plot area. */
export function barHeightPercent(seconds: number, maxSeconds: number): number {
  const max = Math.max(1, maxSeconds);
  return Math.max(BAR_MIN_PCT, Math.min(BAR_MAX_PCT, Math.round((seconds / max) * 100)));
}

/** Short weekday labels for a run of day keys, locale-aware (Persian names in fa). */
export function weekdayLabels(locale: string, dayKeys: string[]): string[] {
  return dayKeys.map((key) =>
    new Intl.DateTimeFormat(locale === "en" ? "en-GB" : locale, { weekday: "short" }).format(new Date(`${key}T12:00:00`))
  );
}
